import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";

const getColumnKey = (column = {}) => String(column.field || column.headerName || "").trim();

export default function ReportColumnVisibilityButton({
  columns = [],
  hiddenColumns = [],
  onHiddenColumnsChange,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const normalizedColumns = useMemo(
    () => (Array.isArray(columns) ? columns : [])
      .map((column) => ({ ...column, __columnKey: getColumnKey(column) }))
      .filter((column) => column.__columnKey),
    [columns],
  );

  const hiddenSet = useMemo(() => new Set((Array.isArray(hiddenColumns) ? hiddenColumns : []).map(String)), [hiddenColumns]);
  const visibleCount = normalizedColumns.filter((column) => !hiddenSet.has(column.__columnKey)).length;

  const toggleColumn = (columnKey) => {
    if (typeof onHiddenColumnsChange !== "function") return;

    onHiddenColumnsChange((previous) => {
      const prevHidden = Array.isArray(previous) ? previous.map(String) : [];
      const nextHidden = new Set(prevHidden);
      if (nextHidden.has(columnKey)) {
        nextHidden.delete(columnKey);
      } else if (visibleCount > 1) {
        nextHidden.add(columnKey);
      }
      return Array.from(nextHidden);
    });
  };

  const showAll = () => {
    if (typeof onHiddenColumnsChange === "function") onHiddenColumnsChange([]);
  };

  return (
    <>
      <Tooltip title="Show / hide columns">
        <IconButton
          size="small"
          aria-label="Show / hide columns"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ color: "text.secondary" }}
        >
          <ViewColumnIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { minWidth: 240, borderRadius: 2 } } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Columns
          </Typography>
          <Typography variant="caption" color="text.secondary">
            At least one column must remain visible.
          </Typography>
        </Box>
        <Box sx={{ px: 2, pb: 1, display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Button size="small" onClick={showAll} sx={{ textTransform: "none", minWidth: 0 }}>
            Show all
          </Button>
          <Button size="small" onClick={showAll} sx={{ textTransform: "none", minWidth: 0 }}>
            Reset
          </Button>
        </Box>
        <Divider />
        {normalizedColumns.map((column) => {
          const columnKey = column.__columnKey;
          const checked = !hiddenSet.has(columnKey);
          return (
            <MenuItem key={columnKey} onClick={() => toggleColumn(columnKey)} dense>
              <Checkbox size="small" checked={checked} sx={{ mr: 1 }} />
              {column.headerName || column.field || columnKey}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
